import React, { useState, useRef, useCallback, useMemo } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/react';
import {
  ReactCompareSlider,
  ReactCompareSliderImage,
} from 'react-compare-slider';
import { ChangeDetail, ChangedRegion } from '../../../types/knowledgeMonitoring';

interface InteractiveDiffViewerProps {
  currentImageUrl: string;
  previousImageUrl?: string;
  diffImageUrl?: string;
  changeDetails: ChangeDetail[];
  changedRegions: ChangedRegion[];
  imageWidth: number;
  imageHeight: number;
}

const COLOR_MAP: Record<string, { stroke: string; fill: string }> = {
  major: { stroke: '#EF4444', fill: 'rgba(239,68,68,0.3)' },
  moderate: { stroke: '#3B82F6', fill: 'rgba(59,130,246,0.3)' },
  minor: { stroke: '#10B981', fill: 'rgba(16,185,129,0.3)' },
};

const DETAIL_TYPE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  visual: { bg: 'bg-purple/10', text: 'text-purple', border: 'border-purple/20' },
  content: { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/20' },
  structure: { bg: 'bg-pink/10', text: 'text-pink', border: 'border-pink/20' },
};

const IMPORTANCE_COLORS: Record<string, string> = {
  high: 'bg-red-500 text-white',
  medium: 'bg-yellow-500 text-white',
  low: 'bg-green-500 text-white',
};

const CHANGE_TYPE_LABELS: Record<string, string> = {
  visual: '視覚的変更',
  content: 'コンテンツ変更',
  structure: '構造変更',
};

const IMPORTANCE_LABELS: Record<string, string> = {
  high: '高',
  medium: '中',
  low: '低',
};

const REGION_TYPE_LABELS: Record<string, string> = {
  major: '大きな変更',
  moderate: '中程度の変更',
  minor: '小さな変更',
};

const InteractiveDiffViewer: React.FC<InteractiveDiffViewerProps> = ({
  currentImageUrl,
  previousImageUrl,
  diffImageUrl,
  changeDetails,
  changedRegions,
  imageWidth,
  imageHeight,
}) => {
  const [hoveredRegion, setHoveredRegion] = useState<number | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'interactive' | 'slider'>('interactive');

  const listItemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Floating tooltip
  const { refs, floatingStyles } = useFloating({
    open: hoveredRegion !== null,
    placement: 'top',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // Map changed regions to change details by bounding_box matching or index
  const regionToDetailMap = useMemo(() => {
    const map: Record<number, number> = {};
    changedRegions.forEach((region, regionIdx) => {
      // Try to match by bounding_box coordinates
      const detailIdx = changeDetails.findIndex((detail) => {
        if (!detail.bounding_box) return false;
        return (
          detail.bounding_box.x === region.x &&
          detail.bounding_box.y === region.y &&
          detail.bounding_box.width === region.width &&
          detail.bounding_box.height === region.height
        );
      });
      if (detailIdx !== -1) {
        map[regionIdx] = detailIdx;
      } else if (regionIdx < changeDetails.length) {
        // Fallback: map by index
        map[regionIdx] = regionIdx;
      }
    });
    return map;
  }, [changedRegions, changeDetails]);

  const detailToRegionMap = useMemo(() => {
    const map: Record<number, number> = {};
    Object.entries(regionToDetailMap).forEach(([regionIdx, detailIdx]) => {
      map[detailIdx] = Number(regionIdx);
    });
    return map;
  }, [regionToDetailMap]);

  const handleRegionClick = useCallback((regionIndex: number) => {
    setSelectedRegion((prev) => (prev === regionIndex ? null : regionIndex));
    const detailIdx = regionToDetailMap[regionIndex];
    if (detailIdx !== undefined && listItemRefs.current[detailIdx]) {
      listItemRefs.current[detailIdx]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [regionToDetailMap]);

  const handleDetailClick = useCallback((detailIndex: number) => {
    const regionIdx = detailToRegionMap[detailIndex];
    if (regionIdx !== undefined) {
      setSelectedRegion((prev) => (prev === regionIdx ? null : regionIdx));
    }
  }, [detailToRegionMap]);

  const getTooltipData = useCallback((regionIndex: number) => {
    const region = changedRegions[regionIndex];
    const detailIdx = regionToDetailMap[regionIndex];
    const detail = detailIdx !== undefined ? changeDetails[detailIdx] : null;
    return { region, detail };
  }, [changedRegions, changeDetails, regionToDetailMap]);

  // Determine effective image dimensions (fallback to defaults)
  const effectiveWidth = imageWidth || 1280;
  const effectiveHeight = imageHeight || 800;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: '4px' }}>
        <button
          onClick={() => setViewMode('interactive')}
          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
            viewMode === 'interactive'
              ? 'bg-primary text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          インタラクティブ
        </button>
        {previousImageUrl && (
          <button
            onClick={() => setViewMode('slider')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              viewMode === 'slider'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            スライダー比較
          </button>
        )}
      </div>

      {viewMode === 'slider' && previousImageUrl ? (
        /* Slider comparison mode */
        <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
          <ReactCompareSlider
            itemOne={
              <ReactCompareSliderImage
                src={previousImageUrl}
                alt="Before"
              />
            }
            itemTwo={
              <ReactCompareSliderImage
                src={currentImageUrl}
                alt="After"
              />
            }
            style={{ width: '100%', height: 'auto' }}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '8px 12px',
            backgroundColor: '#f9fafb',
            borderTop: '1px solid #e5e7eb',
            fontSize: '12px',
            color: '#6b7280',
          }}>
            <span>前回 (Before)</span>
            <span>今回 (After)</span>
          </div>
        </div>
      ) : (
        /* Interactive overlay mode */
        <div style={{ display: 'flex', gap: '12px', minHeight: '400px' }}>
          {/* Left panel: Image with SVG overlay (70%) */}
          <div style={{
            flex: '7',
            minWidth: 0,
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            position: 'relative',
          }}>
            <TransformWrapper
              initialScale={1}
              minScale={0.5}
              maxScale={4}
              wheel={{ step: 0.1 }}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  {/* Zoom controls */}
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    zIndex: 10,
                    display: 'flex',
                    gap: '4px',
                  }}>
                    <button
                      onClick={() => zoomIn()}
                      className="bg-white border border-warm-border rounded px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 shadow-sm"
                    >
                      +
                    </button>
                    <button
                      onClick={() => zoomOut()}
                      className="bg-white border border-warm-border rounded px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 shadow-sm"
                    >
                      -
                    </button>
                    <button
                      onClick={() => resetTransform()}
                      className="bg-white border border-warm-border rounded px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 shadow-sm"
                    >
                      リセット
                    </button>
                  </div>

                  <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={currentImageUrl}
                        alt="Current screenshot"
                        style={{ width: '100%', display: 'block' }}
                        draggable={false}
                      />
                      <svg
                        viewBox={`0 0 ${effectiveWidth} ${effectiveHeight}`}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          pointerEvents: 'none',
                        }}
                      >
                        {changedRegions.map((region, i) => {
                          const colors = COLOR_MAP[region.change_type] || COLOR_MAP.minor;
                          const isHovered = hoveredRegion === i;
                          const isSelected = selectedRegion === i;
                          return (
                            <rect
                              key={i}
                              x={region.x}
                              y={region.y}
                              width={region.width}
                              height={region.height}
                              stroke={colors.stroke}
                              strokeWidth={isHovered || isSelected ? 3 : 2}
                              fill={isHovered || isSelected ? colors.fill : 'transparent'}
                              style={{
                                cursor: 'pointer',
                                pointerEvents: 'all',
                                transition: 'fill 0.15s ease, stroke-width 0.15s ease',
                              }}
                              onMouseEnter={(e) => {
                                setHoveredRegion(i);
                                refs.setReference(e.currentTarget);
                              }}
                              onMouseLeave={() => {
                                setHoveredRegion(null);
                              }}
                              onClick={() => handleRegionClick(i)}
                            />
                          );
                        })}
                      </svg>
                    </div>
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>

            {/* Floating tooltip */}
            {hoveredRegion !== null && (() => {
              const { region, detail } = getTooltipData(hoveredRegion);
              if (!region) return null;
              return (
                <div
                  ref={refs.setFloating}
                  style={{
                    ...floatingStyles,
                    zIndex: 50,
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #e5e7eb',
                    padding: '10px 14px',
                    maxWidth: '280px',
                    fontSize: '12px',
                    pointerEvents: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <span style={{
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: (COLOR_MAP[region.change_type] || COLOR_MAP.minor).stroke,
                    }} />
                    <span style={{ fontWeight: 600, color: '#374151' }}>
                      {REGION_TYPE_LABELS[region.change_type] || region.change_type}
                    </span>
                    <span style={{ color: '#6b7280', marginLeft: 'auto' }}>
                      {(region.diff_percentage ?? 0).toFixed(1)}%
                    </span>
                  </div>
                  {detail && (
                    <>
                      <div style={{ color: '#4b5563', marginBottom: '4px', lineHeight: '1.4' }}>
                        {detail.description}
                      </div>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <span style={{
                          padding: '1px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 500,
                          backgroundColor: detail.type === 'visual' ? '#f3e8ff' : detail.type === 'content' ? '#dbeafe' : '#e0e7ff',
                          color: detail.type === 'visual' ? '#7c3aed' : detail.type === 'content' ? '#2563eb' : '#4f46e5',
                        }}>
                          {CHANGE_TYPE_LABELS[detail.type] || detail.type}
                        </span>
                        <span style={{
                          padding: '1px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 600,
                          backgroundColor: detail.importance === 'high' ? '#ef4444' : detail.importance === 'medium' ? '#eab308' : '#22c55e',
                          color: 'white',
                        }}>
                          {IMPORTANCE_LABELS[detail.importance] || detail.importance}
                        </span>
                      </div>
                      {detail.location && (
                        <div style={{ color: '#9ca3af', marginTop: '4px', fontSize: '11px' }}>
                          {detail.location}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Right panel: Change details list (30%) */}
          <div style={{
            flex: '3',
            minWidth: 0,
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
          }}>
            <div style={{
              padding: '10px 14px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              borderRadius: '8px 8px 0 0',
            }}>
              <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                変更一覧 ({changeDetails.length}件)
              </h4>
            </div>
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '8px',
            }}>
              {changeDetails.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                  変更詳細なし
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {changeDetails.map((detail, index) => {
                    const regionIdx = detailToRegionMap[index];
                    const isHighlighted = regionIdx !== undefined && (selectedRegion === regionIdx || hoveredRegion === regionIdx);
                    const typeColors = DETAIL_TYPE_COLORS[detail.type] || DETAIL_TYPE_COLORS.visual;

                    return (
                      <div
                        key={index}
                        ref={(el) => { listItemRefs.current[index] = el; }}
                        onClick={() => handleDetailClick(index)}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '6px',
                          border: `1px solid ${isHighlighted ? '#3b82f6' : '#e5e7eb'}`,
                          backgroundColor: isHighlighted ? '#eff6ff' : '#ffffff',
                          cursor: regionIdx !== undefined ? 'pointer' : 'default',
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={() => {
                          if (regionIdx !== undefined) {
                            setHoveredRegion(regionIdx);
                          }
                        }}
                        onMouseLeave={() => {
                          setHoveredRegion(null);
                        }}
                      >
                        <div style={{ display: 'flex', gap: '4px', marginBottom: '6px', flexWrap: 'wrap' }}>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${typeColors.bg} ${typeColors.text} ${typeColors.border}`}>
                            {CHANGE_TYPE_LABELS[detail.type] || detail.type}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${IMPORTANCE_COLORS[detail.importance] || 'bg-gray-500 text-white'}`}>
                            {IMPORTANCE_LABELS[detail.importance] || detail.importance}
                          </span>
                        </div>
                        <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#374151', lineHeight: '1.5' }}>
                          {detail.description}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#9ca3af' }}>
                          <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{detail.location}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveDiffViewer;
